import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Play, Film, Share2, Link2, Check, Download, Smartphone, Copy, CheckCheck } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import VideoModal from '@/components/common/VideoModal';
import VideoThumbnail from '@/components/common/VideoThumbnail';
import ShareButtons from '@/components/ShareButtons';
import { VIDEO_LIBRARY } from '@/data/videoData';
import { SocialImage, videoPosterSrc } from '@/components/SocialImage';

const API = process.env.REACT_APP_BACKEND_URL;
const ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';

const VideoShareButton = ({ videoId, title, description }) => {
  const [open, setOpen] = useState(false);
  const url = `${ORIGIN}/resources/videos/${videoId}`;

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}
        className="p-2 rounded-lg bg-white/90 hover:bg-white text-slate-600 hover:text-taxera-blue shadow-sm transition-all"
        title="Share this video"
        data-testid={`share-btn-${videoId}`}
      >
        <Share2 className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-xl border border-slate-200 p-3 min-w-[200px]"
            onClick={(e) => e.stopPropagation()} data-testid={`share-popover-${videoId}`}>
            <p className="text-2xs font-bold text-slate-400 uppercase tracking-wider mb-2">Share</p>
            <ShareButtons url={url} title={title} summary={description} compact />
          </div>
        </>
      )}
    </div>
  );
};

const CopyLinkButton = ({ videoId }) => {
  const [copied, setCopied] = useState(false);
  const url = `${ORIGIN}/resources/videos/${videoId}`;

  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="p-2 rounded-lg bg-white/90 hover:bg-white text-slate-600 hover:text-taxera-blue shadow-sm transition-all"
      title={copied ? 'Copied!' : 'Copy link'}
      data-testid={`copy-link-${videoId}`}
    >
      {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Link2 className="w-4 h-4" />}
    </button>
  );
};

const VideoLibraryPage = () => {
  const { t } = useTranslation();
  const [activeVideo, setActiveVideo] = useState(null);
  const [dbVideos, setDbVideos] = useState(null);
  const [shorts, setShorts] = useState([]);
  const [copiedCaption, setCopiedCaption] = useState(null);
  const [summerName, setSummerName] = useState('');
  const [summerEmail, setSummerEmail] = useState('');
  const [summerNotifyStatus, setSummerNotifyStatus] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only
  useEffect(() => {
    fetch(`${API}/api/videos`)
      .then(r => r.json())
      .then(data => {
        if (data.videos && data.videos.length > 0) {
          setDbVideos(data.videos.map(v => ({
            ...v,
            id: v.video_id,
            icon: Film,
            src: v.src?.startsWith('/') ? `${API}${v.src}` : v.src,
          })));
        }
      })
      .catch(() => {});
  }, []);

  // Fetch available shorts
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only
  useEffect(() => {
    fetch(`${API}/api/shorts`)
      .then(r => r.json())
      .then(data => { if (data.shorts) setShorts(data.shorts); })
      .catch(() => {});
  }, []);

  const videoList = dbVideos || VIDEO_LIBRARY;
  const featured = videoList.find(v => v.featured);
  // Build per-category lists so the page reads as 3 distinct sections.
  // Featured is rendered separately at the top — exclude it from the Brand grid.
  const brandVideos = videoList.filter(v => v.category === 'Brand' && !v.featured);
  const summerVideos = videoList.filter(v => v.category === "Summer '26");

  // Reusable card for the Brand & Summer '26 grids
  const VideoCard = ({ video, i }) => (
    <motion.div
      key={video.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.05 }}
    >
      <div className="group" data-testid={`video-card-${video.id}`}>
        <Link
          to={`/resources/videos/${video.id}`}
          className="relative block w-full aspect-video rounded-xl overflow-hidden bg-slate-900 mb-3 cursor-pointer hover:shadow-xl transition-all"
        >
          <SocialImage
            src={videoPosterSrc(video.src, true)}
            thumbs={false}
            alt={video.title}
            width={640}
            height={360}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            testId={`video-poster-${video.id}`}
          />
          <div className="absolute inset-0" style={{
            background: `linear-gradient(135deg, ${video.color}15 0%, ${video.color}08 100%)`,
          }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/25 group-hover:scale-110 transition-all">
              <Play className="w-4.5 h-4.5 text-white ml-0.5" fill="white" />
            </div>
          </div>
          <div className="absolute bottom-2 right-2">
            <span className="text-2xs px-2 py-0.5 bg-black/60 rounded text-white/80 font-medium">{video.duration}</span>
          </div>
          <div className="absolute top-3 left-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${video.color}25` }}>
              <video.icon className="w-3.5 h-3.5" style={{ color: video.color }} />
            </div>
          </div>
          <div className="absolute top-3 right-3 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <CopyLinkButton videoId={video.id} />
            <VideoShareButton videoId={video.id} title={video.title} description={video.description} />
          </div>
        </Link>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xs font-semibold uppercase tracking-wide" style={{ color: video.color }}>{video.category}</span>
            </div>
            <Link to={`/resources/videos/${video.id}`} className="block">
              <h3 className="text-sm font-bold text-taxera-charcoal group-hover:text-taxera-blue transition-colors">{video.title}</h3>
            </Link>
            <p className="text-xs text-slate-400 mt-1 line-clamp-2">{video.description}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0 mt-1">
            <CopyLinkButton videoId={video.id} />
            <VideoShareButton videoId={video.id} title={video.title} description={video.description} />
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <PageLayout>
      <div className="max-w-6xl mx-auto px-6 py-16" data-testid="video-library-page">
        {/* Hero */}
        <div className="text-center mb-12">
          <span className="text-xs font-bold text-taxera-teal uppercase tracking-widest">{t('videos.badge')}</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-taxera-charcoal mt-3 mb-4">
            {t('videos.title')}
          </h1>
          <p className="text-base text-slate-500 max-w-2xl mx-auto">
            {t('videos.subtitle')}
          </p>
        </div>

        {/* Featured video */}
        {featured && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="mb-16"
          >
            <div className="bg-slate-900 rounded-2xl overflow-hidden" data-testid="featured-video">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Video side */}
                <Link
                  to={`/resources/videos/${featured.id}`}
                  className="relative aspect-video group cursor-pointer block"
                  data-testid="featured-video-play"
                >
                  <SocialImage
                    src={videoPosterSrc(featured.src)}
                    thumbs={false}
                    eager
                    alt={featured.title}
                    width={1280}
                    height={720}
                    className="absolute inset-0 w-full h-full object-cover"
                    testId="featured-video-poster"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-taxera-blue/20 to-taxera-teal/20" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/25 group-hover:scale-110 transition-all">
                      <Play className="w-7 h-7 text-white ml-1" fill="white" />
                    </div>
                  </div>
                  <div className="absolute bottom-3 left-3">
                    <span className="text-2xs px-2 py-1 bg-white/10 rounded-full text-white/70 font-medium">{featured.duration}</span>
                  </div>
                </Link>
                {/* Info side */}
                <div className="p-8 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-taxera-teal/10 rounded-full w-fit mb-4">
                    <Film className="w-3 h-3 text-taxera-teal" />
                    <span className="text-2xs font-bold text-taxera-teal uppercase tracking-wide">{t('videos.featured')}</span>
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-3">{featured.title}</h2>
                  {featured.tagline && (
                    <p className="text-sm font-semibold text-taxera-teal italic mb-3">"{featured.tagline}"</p>
                  )}
                  <p className="text-sm text-slate-400 leading-relaxed mb-6">{featured.description}</p>
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/resources/videos/${featured.id}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 taxera-gradient text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all"
                      data-testid="featured-watch-btn"
                    >
                      <Play className="w-3.5 h-3.5" /> {t('videos.watchNow')}
                    </Link>
                    <VideoShareButton videoId={featured.id} title={featured.title} description={featured.description} />
                    <CopyLinkButton videoId={featured.id} />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Brand Films Section ── */}
        <div data-testid="brand-films-section">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-taxera-charcoal">Brand Films</h2>
              <p className="text-sm text-slate-500 mt-1">Stories about getting your time back.</p>
            </div>
            <span className="text-xs text-slate-400">{brandVideos.length} {brandVideos.length === 1 ? 'film' : 'films'}</span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {brandVideos.map((video, i) => (
              <VideoCard key={video.id} video={video} i={i} />
            ))}
          </div>
        </div>

        {/* ── Summer '26 Series Section ── */}
        {summerVideos.length > 0 ? (
          <div className="mt-20" data-testid="summer-series-section">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xs font-bold uppercase tracking-widest text-amber-500 bg-amber-400/10 px-3 py-1 rounded-full">Series</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-taxera-charcoal">Summer '26 Series</h2>
                <p className="text-sm italic text-taxera-teal mt-1">"Life After Compliance"</p>
                <p className="text-sm text-slate-500 mt-2 max-w-xl">
                  Lighter. Warmer. Real people, real moments — because when compliance is handled, life just flows.
                </p>
              </div>
              <span className="text-xs text-slate-400 shrink-0">{summerVideos.length} of 10 released</span>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {summerVideos.map((video, i) => (
                <VideoCard key={video.id} video={video} i={i} />
              ))}
            </div>
          </div>
        ) : null}

        {/* ── Shorts Section ── */}
        {shorts.length > 0 && (
          <div className="mt-20" data-testid="shorts-section">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-pink-500/10 to-violet-500/10 rounded-full mb-4">
                <Smartphone className="w-3.5 h-3.5 text-pink-500" />
                <span className="text-xs font-bold text-pink-600 uppercase tracking-wider">Vertical Videos for Social</span>
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-taxera-charcoal mb-3">
                Shorts
              </h2>
              <p className="text-sm text-slate-500 max-w-lg mx-auto">
                9:16 vertical format — optimized for LinkedIn, Instagram, and TikTok. Download and post directly.
              </p>
            </div>

            {/* Brand Shorts */}
            <div className="mb-14">
              <div className="flex items-center gap-3 mb-6">
                <h3 className="font-heading text-lg font-bold text-taxera-charcoal">Brand Shorts</h3>
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400 font-medium">{shorts.length} videos</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
                {shorts.map((short, i) => {
                  const videoMeta = videoList.find(v => v.id === short.video_id);
                  if (!videoMeta) return null;

                  const caption = `${videoMeta.tagline || videoMeta.title}\n\n${videoMeta.description}\n\n#TaxTechnology #Compliance #Taxera`;
                  const videoPageUrl = `${window.location.origin}/resources/videos/${videoMeta.id}`;
                  const fullCaption = `${caption}\n\n${videoPageUrl}`;

                return (
                  <motion.div
                    key={short.video_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 }}
                    className="group"
                    data-testid={`short-card-${short.video_id}`}
                  >
                    {/* Vertical preview */}
                    <div className="relative aspect-[9/16] rounded-2xl overflow-hidden bg-slate-900 mb-3 shadow-lg group-hover:shadow-xl transition-all">
                      <video
                        src={`${API}${short.download_url}`}
                        poster={videoPosterSrc(short.download_url, true) || undefined}
                        preload="none"
                        muted
                        playsInline
                        className="w-full h-full object-cover"
                        onMouseEnter={(e) => e.target.play()}
                        onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                        data-testid={`short-video-${short.video_id}`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30 pointer-events-none" />
                      <div className="absolute top-3 left-3 right-3">
                        <span className="text-2xs font-bold text-white/80 uppercase tracking-wider">{videoMeta.category}</span>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
                        </div>
                      </div>
                      <div className="absolute bottom-3 left-3 right-3">
                        <p className="text-xs font-bold text-white truncate">{videoMeta.title}</p>
                        <p className="text-2xs text-white/70 mt-0.5">{short.size_mb} MB · 9:16</p>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <a
                        href={`${API}${short.download_url}`}
                        download={`${short.video_id}_short.mp4`}
                        className="flex items-center justify-center gap-1.5 w-full py-2 rounded-lg bg-taxera-charcoal text-white text-xs font-semibold hover:bg-taxera-blue transition-colors"
                        data-testid={`short-download-${short.video_id}`}
                      >
                        <Download className="w-3.5 h-3.5" /> Download
                      </a>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(fullCaption);
                          setCopiedCaption(short.video_id);
                          setTimeout(() => setCopiedCaption(null), 2000);
                        }}
                        className="flex items-center justify-center gap-1.5 w-full py-2 rounded-lg bg-slate-100 text-slate-600 text-xs font-semibold hover:bg-slate-200 transition-colors"
                        data-testid={`short-caption-${short.video_id}`}
                      >
                        {copiedCaption === short.video_id ? (
                          <><CheckCheck className="w-3.5 h-3.5 text-emerald-500" /> Copied!</>
                        ) : (
                          <><Copy className="w-3.5 h-3.5" /> Copy Caption</>
                        )}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
              </div>
            </div>
          </div>
        )}

        {/* ── Summer '26 Series — Teaser (only when no Summer videos are published) ── */}
        {summerVideos.length === 0 && (
          <div className="mt-20" data-testid="summer-series-teaser">
            <div className="rounded-2xl overflow-hidden bg-slate-900 relative">
              {/* Warm gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-taxera-teal/10 pointer-events-none" />
              <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-amber-400/[0.06] to-transparent pointer-events-none" />

              <div className="relative p-8 sm:p-12 lg:p-16">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-2xs font-bold uppercase tracking-widest text-amber-400 bg-amber-400/10 px-3 py-1 rounded-full">Coming Soon</span>
                  <div className="h-px flex-1 bg-white/10" />
                </div>

                <h3 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-2">
                  Summer '26 Series
                </h3>
                <p className="text-lg text-taxera-teal italic font-semibold mb-4">
                  "Life After Compliance"
                </p>
                <p className="text-sm text-slate-400 max-w-xl mb-8">
                  10 new films. Lighter. Warmer. Shot for social. Real people, real moments — because when compliance is handled, life just flows.
                </p>

                {/* Title grid */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-6 gap-y-2 mb-10">
                  {[
                    'The Rooftop Lunch', 'The Friday Flight', 'The Morning Swim',
                    'The Park', 'The Balcony', 'The Long Table',
                    'The Festival', 'The Bike Ride', 'The Golden Hour Walk',
                    'The Gelato Queue'
                  ].map((title, i) => (
                    <div key={`summer-${title}`} className="flex items-center gap-2">
                      <span className="text-2xs text-slate-500 font-mono w-4">{String(i + 1).padStart(2, '0')}</span>
                      <span className="text-sm text-slate-300">{title}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="bg-white/[0.06] backdrop-blur-sm rounded-xl border border-white/10 p-6 max-w-xl" data-testid="summer-notify-card">
                  <p className="text-sm text-white font-semibold mb-1">
                    Join the Taxera community
                  </p>
                  <p className="text-xs text-slate-400 mb-4">
                    Be the first to watch the Summer '26 series when it drops.
                  </p>
                  {summerNotifyStatus === 'success' ? (
                    <div className="flex items-center gap-2 text-sm font-semibold text-emerald-400" data-testid="summer-notify-success">
                      <CheckCheck className="w-4 h-4" /> You're in! We'll notify you when it's ready.
                    </div>
                  ) : (
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      if (!summerEmail) return;
                      setSummerNotifyStatus('loading');
                      try {
                        const res = await fetch(`${API}/api/notify/product-videos`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ name: summerName, email: summerEmail, hub: 'summer-26-series' }),
                        });
                        if (res.ok) setSummerNotifyStatus('success');
                        else setSummerNotifyStatus('error');
                      } catch { setSummerNotifyStatus('error'); }
                    }} className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        value={summerName}
                        onChange={e => setSummerName(e.target.value)}
                        placeholder="First name"
                        className="px-3 py-2.5 rounded-lg bg-white/10 border border-white/10 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-taxera-teal w-full sm:w-32"
                        data-testid="summer-notify-name"
                      />
                      <input
                        type="email"
                        value={summerEmail}
                        onChange={e => setSummerEmail(e.target.value)}
                        placeholder="Email"
                        required
                        className="px-3 py-2.5 rounded-lg bg-white/10 border border-white/10 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-taxera-teal flex-1"
                        data-testid="summer-notify-email"
                      />
                      <button
                        type="submit"
                        disabled={summerNotifyStatus === 'loading'}
                        className="px-5 py-2.5 rounded-lg bg-amber-400 text-slate-900 text-sm font-bold hover:bg-amber-300 transition-all disabled:opacity-50 whitespace-nowrap"
                        data-testid="summer-notify-btn"
                      >
                        {summerNotifyStatus === 'loading' ? 'Joining...' : 'Get notified'}
                      </button>
                    </form>
                  )}
                  {summerNotifyStatus === 'error' && (
                    <p className="text-xs text-red-400 mt-2">Something went wrong. Please try again.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Video Modal */}
      <VideoModal
        isOpen={!!activeVideo}
        onClose={() => setActiveVideo(null)}
        video={activeVideo}
      />
    </PageLayout>
  );
};

export default VideoLibraryPage;
